import type { ColorScheme } from '@unifyapps/defs/types/interface';
import AsyncView from '../../../../components/AsyncView';
import { debugSSR } from '../../../../consts/debug';
import { WITH_SUSPENSE } from '../../../const';
import type { UseFetchInterfaceAppResourcesHookType } from '../../../hooks/fetchAndInvalidation/types';
import { InterfaceAppProviders } from '../InterfaceAppProviders';
import type { InterfaceAppProvidersProps } from '../InterfaceAppProviders/InterfaceAppProviders';

type InterfaceAppResourceLoaderProps = {
  children: React.ReactNode;
  response: ReturnType<UseFetchInterfaceAppResourcesHookType>;
  client: InterfaceAppProvidersProps['client'];
  mode: InterfaceAppProvidersProps['mode'];
  registry: InterfaceAppProvidersProps['registry'];
  locale?: InterfaceAppProvidersProps['locale'];
  deviceDetails?: InterfaceAppProvidersProps['deviceDetails'];
  basePath?: string;
  renderLoader?: () => React.ReactNode;
  renderError?: (error?: Error) => React.ReactNode;
  colorScheme?: ColorScheme;
  interfaceThemeId?: string;
  appVariantKey?: string;
};

function InterfaceAppResourceLoader(props: InterfaceAppResourceLoaderProps) {
  const {
    client,
    children,
    mode,
    registry,
    response,
    basePath,
    deviceDetails,
    renderLoader,
    renderError,
    colorScheme,
    interfaceThemeId,
    locale,
    appVariantKey,
  } = props;

  const {
    interfaceRecord,
    isLoading,
    error,
    permissions,
    envVariables,
    commonNamespace,
    dataSources,
    globalInterfacePage,
    customBlocks,
    parentInterfaceRecord,
  } = response;

  debugSSR(
    'InterfaceAppResourceLoader',
    {
      isLoading,
    },
    {
      interfaceId: interfaceRecord?.id,
      extraInfo: {
        hasError: Boolean(error),
        hasInterfaceRecord: Boolean(interfaceRecord),
      },
    },
  );

  /**
   * Passing locale=locale from here, since in this component, we do not care about the locale, it's just passing through
   * This is to ensure that the user context is correctly initialized.
   * If we take from common namespace, and if common namespace is not set, then we take from the interface record default value, which could be incorrect.
   * In HDFC, DPDP, common namespace wasn't set, and issue was coming due to switching locales,
   * In DDA, if usercontext.locale is used in builder, it wasn't working as expected.
   * Discuss more @prakhar8423 and @abhinavsingi, if needed
   */

  return (
    <AsyncView
      data={interfaceRecord}
      error={error}
      isLoading={isLoading}
      renderError={renderError}
      renderLoader={renderLoader}
      withSuspense={WITH_SUSPENSE}
    >
      {interfaceRecord && (
        <InterfaceAppProviders
          appVariantKey={appVariantKey}
          basePath={basePath}
          client={client}
          colorScheme={colorScheme}
          commonNamespace={commonNamespace}
          customBlocks={customBlocks}
          dataSources={dataSources}
          deviceDetails={deviceDetails}
          envVariables={envVariables}
          globalInterfacePage={globalInterfacePage}
          interfaceRecord={interfaceRecord}
          interfaceThemeId={interfaceThemeId}
          locale={locale}
          mode={mode}
          parentInterfaceRecord={parentInterfaceRecord}
          permissions={permissions}
          registry={registry}
        >
          {children}
        </InterfaceAppProviders>
      )}
    </AsyncView>
  );
}

export default InterfaceAppResourceLoader;
